import { Card, Typography, useTheme } from '@mui/material';
import { MeasureComparison } from 'components/MeasureComparison';
import { MeasureFormat } from 'components/measure-unit/MeasureFormat';
import { MeasureUnitConfig } from 'components/measure-unit/MeasureUnitConfig';
import { useDataRequest } from 'hooks/useDataRequest';
import { Totals } from 'models/data';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDatePickerLabels } from 'hooks/useDatePickerLabels';
import { Measure } from 'services/model/model.service';

export function MeasureCard({
  totals,
  measure,
  additionalInfo = '',
  additionalTitleId = '',
  selected = false,
  clickHandler = undefined,
  onSelect = clickHandler,
  testid = 'measure-card',
  customComparisonPeriod = undefined,
  detectionDate = undefined,
  hideComparison = false,
  formatConfig = undefined,
}: {
  totals: Totals;
  measure: Measure;
  additionalTitleId?: string;
  additionalInfo?: string;
  selected?: boolean;
  clickHandler?: () => void;
  onSelect?: () => void;
  testid?: string;
  customComparisonPeriod?: string;
  detectionDate?: string;
  hideComparison?: boolean;
  formatConfig?: MeasureUnitConfig;
}) {
  const theme = useTheme();
  const selectable = !!onSelect;
  const intl = useIntl();
  const requestData = useDataRequest();
  const dateRangeLabelContext = useDatePickerLabels();

  const dateRange =
    customComparisonPeriod ??
    (requestData.timeFilter?.fromDate &&
    requestData.comparisonTimeFilter?.fromDate &&
    requestData.timeFilter.fromDate !== requestData.comparisonTimeFilter?.fromDate
      ? dateRangeLabelContext.translatedMeasureCardComparisonLabel(intl)
      : undefined);

  return (
    <Card
      onClick={onSelect}
      className={`bg-snow-50 transition-all duration-200 ${selectable ? 'hover:bg-denim-50 hover:shadow-elevation-medium' : ''}`}
      sx={{
        // We currently still have to inject our own elevation theme until
        // this component has been styled in TB.Desy
        cursor: selectable ? 'pointer' : 'default',
        transitionDuration: theme.transitions.duration.standard,
        borderBottom: selected ? `4px solid black` : `4px solid transparent`,
        boxShadow: 0,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'start',
        textAlign: 'left',
        paddingTop: 5,
        paddingBottom: 4,
        paddingX: 5,
        gap: 3,
        height: '100%',
        borderRadius: '8px',
      }}
      data-testid={`${testid}-${measure.code}`}
    >
      <Typography variant="h4" sx={{ minHeight: '2.25rem', height: '100%', fontWeight: 800 }}>
        <div>
          <span>{measure.labelId ? <FormattedMessage id={measure.labelId} /> : measure.metric.name}</span>
          {detectionDate && (
            <span className="text-xs font-normal text-text-secondary text-nowrap pl-2">
              <FormattedMessage id="trading.drilldown.detection.date" defaultMessage="on" /> {detectionDate}
            </span>
          )}
          {additionalTitleId && (
            <span className="text-xs font-normal text-text-secondary text-nowrap pl-2">
              <FormattedMessage id={additionalTitleId} defaultMessage="" />
            </span>
          )}
        </div>
      </Typography>
      <div className="space-y-1 flex flex-col justify-end h-full">
        <div className="flex items-center space-x-2">
          <Typography variant="h1">
            <MeasureFormat value={measure.getValue(totals.values)} unit={measure.metric.unit} config={formatConfig} />
          </Typography>
          <Typography variant="caption2" component="span">
            {additionalInfo}
          </Typography>
        </div>
        {dateRange && !hideComparison && (
          <div className="flex items-center space-x-2">
            <MeasureComparison
              value={measure.getValue(totals.values)}
              comparisonValue={measure.getValue(totals.compareValues)}
              unit={measure.metric.unit}
              interpretation={measure.metric.interpretation ?? 1}
            />
            <Typography variant="caption2" component="span" noWrap={false}>
              {customComparisonPeriod ? (
                dateRange
              ) : (
                <FormattedMessage
                  id="measurecomparison.period"
                  values={{
                    timePeriode: dateRange,
                  }}
                  defaultMessage="from previous period"
                />
              )}
            </Typography>
          </div>
        )}
        {hideComparison && <div className="h-6" />}
      </div>
    </Card>
  );
}
